import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import RaceHeader from "./RaceHeader/RaceHeader";
import RaceTimer from "./RaceTimer/RaceTimer";
import TokenStats from "./TokenStats/TokenStats";
import PlayByPlay from "./PlayByPlay/PlayByPlay";
import ParticipantSection from "./ParticipantSection/ParticipantSection"; // Import the new component
import "./WatchDetail.css";
import WatchLive from "./WatchLive/WatchLive";

const WatchDetail = ({ race, onBack }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [showStats, setShowStats] = useState(false);

  const handleShare = async () => {
    if (!navigator.share) {
      throw new Error("Share functionality not available");
    }

    const leadingToken = race.tokens[0];
    await navigator.share({
      title: `${race.title} - Live Crypto Race`,
      text: `Check out this live race: ${race.title}\nCurrent Leader: ${leadingToken?.symbol
        } (${leadingToken?.percentChange > 0 ? "+" : ""}${leadingToken?.percentChange
        }%)`,
      url: window.location.href,
    });
  };

  const handleToggleFavorite = useCallback(() => {
    setIsFavorite((prev) => !prev);
  }, []);

  const handleToggleStats = useCallback(() => {
    setShowStats((prev) => !prev);
  }, []);

  if (!race) {
    return <div className="watch-detail-error">Race data unavailable</div>;
  }

  return (
    <div className="watch-detail">
      <div className="watch-detail-container">
        <button onClick={onBack} className="back-button-races mb-6">
          ← Back to Races
        </button>
        <div className="flex gap-4">
          {/* Main Content Column */}

          <div className="race-main-content">
            <RaceHeader
              race={race}
              onShare={handleShare}
              onToggleFavorite={handleToggleFavorite}
              isFavorite={isFavorite}
              onToggleStats={handleToggleStats}
              showStats={showStats}
            />
            <div className="race-content-section">
              <div className="text-lg font-bold">Ranking</div>
              <TokenStats tokens={race.tokens} showStats={showStats} />
              <RaceTimer
                elapsedTime={race.elapsedTime}
                remainingTime={race.remainingTime}
                startTime={race.startTime}
                endTime={race.endTime}
              />
              <WatchLive race={race} />
            </div>
            <PlayByPlay race={race} />
          </div>
          <div className="side-panel">
            <div className="panel-section">
              <h3 className="panel-section-title">Race Statistics</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Total Prize Pool</span>
                  <span className="stat-value">
                    ${race.totalPrizePool?.toLocaleString()}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Volume</span>
                  <span className="stat-value">
                    ${race.totalVolume?.toLocaleString()}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Race Type</span>
                  <span className="stat-value">{race.type}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Handicap</span>
                  <span className="stat-value">{race.handicap}</span>
                </div>
              </div>
              <ParticipantSection race={race} />
            </div>

            {/* Add Participant Section */}

          </div>
        </div>

        {/* Side Panel Column */}

      </div>
    </div>
  );
};

WatchDetail.propTypes = {
  race: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    elapsedTime: PropTypes.string.isRequired,
    remainingTime: PropTypes.string.isRequired,
    startTime: PropTypes.string.isRequired,
    endTime: PropTypes.string.isRequired,
    totalPrizePool: PropTypes.number,
    totalVolume: PropTypes.number,
    type: PropTypes.string,
    handicap: PropTypes.string,
    tokens: PropTypes.arrayOf(
      PropTypes.shape({
        symbol: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        percentChange: PropTypes.number.isRequired,
        currentPrice: PropTypes.number.isRequired,
        momentum: PropTypes.number,
      })
    ).isRequired,
    participants: PropTypes.arrayOf(
      PropTypes.shape({
        address: PropTypes.string.isRequired,
        avatar: PropTypes.string.isRequired,
        bet: PropTypes.number.isRequired,
        token: PropTypes.string.isRequired,
        joinedAgo: PropTypes.string.isRequired,
      })
    ).isRequired,
    playByPlay: PropTypes.array,
  }),
  onBack: PropTypes.func.isRequired,
};

export default WatchDetail;
